import { useCallback, useMemo } from "react";

import { compareNaturalText } from "@/lib/compare-natural-text";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { Assignee } from "../../../user/models/assignee-model";
import { Project, ProjectSchema } from "../../models/project-model";

import { getProjectListForWorkspaceQueryDocument } from "./project.gql";

export interface UseGetProjectListForWorkspaceProps {
  workspaceId: string;
  enabled?: boolean;
}

export const useGetProjectListForWorkspace = ({ workspaceId, enabled = true }: UseGetProjectListForWorkspaceProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.projects.list(workspaceId),
    queryFn: async () => {
      if (!workspaceId) {
        throw new Error("Workspace ID is required");
      }

      const client = createOperationClient("GetProjectListForWorkspace");
      const data = await client.request(getProjectListForWorkspaceQueryDocument, {
        input: { workspaceId },
      });

      return data.projectListForWorkspace.map((project) => ProjectSchema.parse(project));
    },
    enabled: enabled && !!workspaceId,
    staleTime: 2 * 60 * 1000, // 2 minutes for list data
  });

  const projectList = useMemo(() => {
    return queryResult.data ?? [];
  }, [queryResult.data]);

  const getProjectListByAssignedUser = useCallback(
    (userId: string) => {
      return projectList
        .slice()
        .filter((project: Project) => project.assigneeList?.some((a: Assignee) => a.userId === userId))
        .sort((a: Project, b: Project) => {
          if (!a || !b) {
            return 0;
          }
          return compareNaturalText(a.title, b.title);
        });
    },
    [projectList],
  );

  const getProjectListByOwnerId = useCallback(
    (ownerId: string, ownerType?: string) => {
      return projectList
        .slice()
        .filter((project: Project) =>
          project.projectInstanceList?.some(
            (projectInstance) =>
              projectInstance.ownerId === ownerId && (!ownerType || projectInstance.ownerType === ownerType),
          ),
        );
    },
    [projectList],
  );

  return {
    projectList,
    getProjectListByAssignedUser,
    getProjectListByOwnerId,
    queryResult,
    loading: queryResult.isLoading,
    error: queryResult.error,
  };
};
